import { sortBy as _sortBy, reject as _reject } from 'lodash-es';
import { useState, useCallback, Fragment, type ReactNode } from 'react';
import Link from 'next/link';
import { useMutation } from '@apollo/client/react';
import { makeStyles } from 'tss-react/mui';
import { red } from '@mui/material/colors';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Schedule from '@mui/icons-material/Schedule';
import ContentCopy from '@mui/icons-material/ContentCopy';
import EditOutlined from '@mui/icons-material/EditOutlined';
import Star from '@mui/icons-material/Star';
import StarBorder from '@mui/icons-material/StarBorder';
import { updateEventGroupFeaturedMutation } from '../../apollo/events';
import { groupQuery } from '../../apollo/groups';
import GroupPendingButton from './group-pending-button';
import CopyEventButton from './copy-event-button';
import CancelEventDialog from './cancel-event-dialog';
import DeleteEventDialog from './delete-event-dialog';
import { eventPath, eventSeriesPath } from '../../utils/slugify';
import type { Event } from '../../types/Event';
import type { Group } from '../../types/Group';

const useStyles = makeStyles()((theme) => ({
  icon: {
    marginLeft: theme.spacing(0.5),
  },
  featuredIcon: {
    color: theme.palette.secondary.main,
  },
}));

type Props = {
  event: Event;
  group?: Group;
  buttonComponent?: React.ElementType;
  children?: ReactNode;
  tooltip?: string;
  [key: string]: unknown;
};

const EventEditDropdown = (props: Props) => {
  const { event, group, buttonComponent: ButtonComponent = Button, children, tooltip, ...rest } = props;
  const { classes, cx } = useStyles();

  const [anchorEl, setAnchorEl] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const handleOpen = useCallback((e) => setAnchorEl(e.currentTarget), []);
  const handleClose = useCallback(() => setAnchorEl(null), []);

  const [updateEventGroupFeatured] = useMutation(updateEventGroupFeaturedMutation);
  const handleToggleGroupFeatured = useCallback(() => {
    updateEventGroupFeatured({
      variables: {
        id: event.id,
        groupFeatured: !event.groupFeatured,
      },
      update: (proxy, { data: { updateEventGroupFeatured: updatedEvent } }) => {
        const groupQueryVariables = {
          slug: event.group.slug,
        };
        const data = proxy.readQuery({
          query: groupQuery,
          variables: groupQueryVariables,
        });
        const featuredUpcomingEvents = updatedEvent.groupFeatured ?
          _sortBy([...data.group.featuredUpcomingEvents, updatedEvent], 'startAt') :
          _reject(data.group.featuredUpcomingEvents, (e) => e.id === updatedEvent.id);
        proxy.writeQuery({
          query: groupQuery,
          variables: groupQueryVariables,
          data: {
            ...data,
            group: {
              ...data.group,
              featuredUpcomingEvents,
            },
          },
        });
      }
    }).then(() => {
      setAnchorEl(null);
    });
  }, [event, updateEventGroupFeatured]);

  const button = (
    <ButtonComponent
      aria-controls={`edit-dropdown-${event.id}`}
      aria-haspopup="true"
      onClick={handleOpen}
      {...rest}
    >
      {children ?? (<>Edit <EditOutlined className={classes.icon} /></>)}
    </ButtonComponent>
  );

  return (
    <Fragment>
      {tooltip ? <Tooltip title={tooltip}>{button}</Tooltip> : button}
      <Menu
        id={`edit-dropdown-${event.id}`}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem
          component={Link}
          href={`${eventPath(event.id, event.name)}/edit`}
        >
          Edit Event
        </MenuItem>
        {event.series ? (
          <MenuItem
            component={Link}
            href={`${eventSeriesPath(event.series.id, event.series.name)}/schedule`}
          >
            Manage Series <Schedule className={classes.icon} />
          </MenuItem>
        ) : (
          <MenuItem
            component={Link}
            href={`/event-series/new?convertEventId=${event.id}`}
          >
            Convert to Series <Schedule className={classes.icon} />
          </MenuItem>
        )}
        <CopyEventButton
          buttonComponent={MenuItem}
          event={event}
        >
          Copy Event <ContentCopy className={classes.icon} />
        </CopyEventButton>
        {group && group.permissions.editable && (
          <MenuItem onClick={handleToggleGroupFeatured}>
            Mark as {event.groupFeatured ? 'Not ' : ''}Featured
            {event.groupFeatured
              ? <StarBorder className={cx(classes.icon, classes.featuredIcon)} />
              : <Star className={cx(classes.icon, classes.featuredIcon)} />}
          </MenuItem>
        )}
        {event.status === 'GROUP_PENDING' && group && group.permissions.editable && (
          <GroupPendingButton
            event={event}
            buttonComponent={MenuItem}
            onSuccess={handleClose}
          >
            Respond To Approval Request
          </GroupPendingButton>
        )}
        <Divider />
        <MenuItem onClick={() => { handleClose(); setCancelDialogOpen(true); }}>
          Cancel Event
        </MenuItem>
        <MenuItem
          onClick={() => { handleClose(); setDeleteDialogOpen(true); }}
          sx={{ color: red.A400 }}
        >
          Delete Event
        </MenuItem>
      </Menu>
      <CancelEventDialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        eventId={event.id}
        eventName={event.name}
      />
      <DeleteEventDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        eventId={event.id}
      />
    </Fragment>
  );
};

export default EventEditDropdown;
